import React, { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useParams } from 'react-router';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import Navbar from 'components/Navbar';
import MatchResultHeader from 'components/matchResultHeader';
import MatchFilter from 'components/matchFilter';
import MatchResults from 'components/matchResults';
import Footer from 'components/Footer/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import { JobService } from 'services/jobs/job';

import './index.scss';
import useLocales from 'hooks/useLocales';

interface MatchResultProps {}

interface ProfileData {
  candidate: any;
  job: any;
}

const MatchResult: FC<MatchResultProps> = (): JSX.Element => {
  const { t } = useLocales();
  const [filter, setFilter] = useState<any>();
  const { id, type } = useParams<{ id: string; type: string }>();
  const { matchProfileOne, matchProfileCandidate }: any = useSelector(
    (state: RootState) => state.match
  );

  const { jobProfileCandidate, singleJob }: any = useSelector(
    (state: RootState) => state.jobs
  );
  const [openModal, setOpenModal] = useState(false);
  const [onUser, setOneUser] = useState<ProfileData | null>(null);

  const transformFilter = (rawFilter: any) => {
    if (!rawFilter) return rawFilter;

    // Split skills into int (IT skills) and str (hard/soft skills)
    const skillInts: number[] = [];
    const skillStrs: string[] = [];
    (rawFilter.skills || []).forEach((skill: any) => {
      if (skill.isComputerSkill && skill.id && !isNaN(Number(skill.id))) {
        skillInts.push(Number(skill.id));
      } else if (skill.name) {
        skillStrs.push(skill.name);
      } else if (typeof skill === 'string') {
        skillStrs.push(skill);
      }
    });

    return {
      ...(rawFilter.name ? { name: rawFilter.name } : {}),

      industries: rawFilter.industry ? [Number(rawFilter.industry)] : [],

      skills: [...skillInts, ...skillStrs],

      locations: (rawFilter.locations || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      compensations: (rawFilter.compensations || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      work_exps: (rawFilter.career_level || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      studies: (rawFilter.studies || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      degrees:
        rawFilter.degree_type && !isNaN(Number(rawFilter.degree_type))
          ? [Number(rawFilter.degree_type)]
          : [],

      languages: (rawFilter.languages || [])
        .map((lang: any) => Number(lang.id || lang))
        .filter(Boolean),

      ...(rawFilter.work_hours
        ? {
            work_hours: (rawFilter.work_hours || [])
              .map((id: any) => Number(id))
              .filter(Boolean)
          }
        : {}),

      contracts: (rawFilter.contract || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      relocations: (rawFilter.relocations || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      availabilities: (rawFilter.availabilities || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      management_exps: (rawFilter.management_exps || [])
        .map((id: any) => Number(id))
        .filter(Boolean),

      ...(rawFilter.min_score
        ? { min_score: Number(rawFilter.min_score) }
        : {}),

      ...(rawFilter.default_lang
        ? { default_lang: Number(rawFilter.default_lang) }
        : {}),
      ...(rawFilter.is_remote ? { is_remote: true } : { is_remote: false })
    };
  };

  const handleFilter = (newFilter: any) => {
    setFilter(transformFilter(newFilter));
  };

  const handleClosePopup = () => {
    setOpenModal(false);
  };

  const handleModal = (open: boolean, id: number) => {
    setOpenModal(open);
    const oneElement = (
      matchProfileCandidate || jobProfileCandidate
    )?.results?.find((el: any) => el.id === id);
    setOneUser({
      candidate: oneElement,
      job: matchProfileOne || singleJob
    });
  };

  const getMatchCandidates = (offset: number = 0, limit: number = 10) => {
    if (id) {
      if (type === 'find-candidate') {
        MatchProfileService.getMatchProfileCandidatesById(
          id,
          { offset, limit },
          filter
        );
      } else {
        JobService.getJobProfileCandidatesById(id, { offset, limit }, filter);
      }
    }
  };

  const getInformationAboutProfile = () => {
    if (type === 'find-candidate') {
      MatchProfileService.getMatchProfileById(id ? +id : 0);
    } else {
      JobService.getJobByIdOrSlug(id ? +id : 0);
    }
  };

  useEffect(() => {
    getInformationAboutProfile();
  }, [id]);

  useEffect(() => {
    getMatchCandidates();
  }, [id, filter]);

  return (
    <>
      <div className="match">
        <Navbar />
        <div className="match-content">
          <MatchResultHeader data={matchProfileOne || singleJob} />
          <div className="content-container">
            <MatchFilter applyFilter={handleFilter} />
            <MatchResults
              filter={filter}
              handleModal={handleModal}
              getMatchCandidates={getMatchCandidates}
              matchProfileCandidate={
                matchProfileCandidate || jobProfileCandidate
              }
            />
          </div>
        </div>
        <div className="footers">
          <Footer />
        </div>
      </div>
      {openModal && (matchProfileOne || singleJob) && (
        <Popup
          className="popUp"
          title={t('Match Profile')}
          description={(matchProfileOne || singleJob)?.title}
          onClose={handleClosePopup}
        >
          <ProfilePopUp
            selectedRequest={onUser}
            getJob={matchProfileOne || singleJob}
          />
        </Popup>
      )}
    </>
  );
};

export default MatchResult;
